<template>
  <div class="applicant-form">
    <nav class="tabs">
      <div
        :class="{tab: true, active: activeSection === 'upload'}"
        type="simple"
        @click="activeSection = 'upload'"
      >
        {{ $t('pages.onboarding.applicant.documents.upload_tab') }}
      </div>
      <div
        :class="{tab: true, active: activeSection === 'documents'}"
        type="simple"
        @click="activeSection = 'documents'"
      >
        {{ $t('pages.onboarding.applicant.documents.documents_tab', {count: documents.length}) }}
      </div>
    </nav>
    <section v-if="activeSection === 'upload'">
      <upload-document-form
        ref="form"
        :form="form"
        :loading="uploading"
        @submit="uploadFile"
      />
    </section>
    <section v-if="activeSection === 'documents'" class="documents">
      <documents-list
        :documents="documents"
        :can-delete="true"
        @delete="onDeleteConfirmation"
      />
    </section>
    <slot name="footer" :invalid="false" :loading="loading" />

    <portal to="modals">
      <t-confirm
        v-if="deleteConfirmationVisible"
        :button-label="$t('global.actions.confirm_deletion')"
        @confirmed="destroy"
        @cancelled="closeModal"
      >
        <template #header>
          {{ $t('global.confirmation.delete') }}
        </template>
        <p>{{ $t('global.confirmation.final_action') }}</p>
      </t-confirm>
    </portal>
  </div>
</template>
<script>
import Vue from 'vue';
import FormBaseMixin from '../../FormBaseMixin';
import ApplicantDocument from '~/models/ApplicantDocument';
import DocumentsList from '~/components/DocumentsList.vue';
import UploadDocumentForm from '~/pages/applicant/account/documents/partials/UploadDocumentForm.vue';

export default Vue.extend({
  components: { DocumentsList, UploadDocumentForm },

  mixins: [FormBaseMixin],

  props: {
    applicant: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    form: {
      label: null,
      file: null,
      terms_accepted: false,
    },

    documents: [],

    activeSection: 'upload',
    uploading: false,
    deletingDocumentId: null,
    deleteConfirmationVisible: false,
  }),

  async created() {
    this.getDocuments();
  },

  methods: {
    async getDocuments(payload) {
      const documents = await new ApplicantDocument().get();
      this.documents = documents.data;
    },

    async uploadFile() {
      this.uploading = true;
      try {
        const document = await new ApplicantDocument({
          applicant_id: this.applicant.id,
          ...this.form,
        }).save();

        this.documents.push(document);

        await this.$notify.success(this.$t('notifications.document.created'));
        this.resetForm();
        this.$refs.form.$refs.validator.reset();
      } catch (e) {
        this.$axios.handleError(e, this.$refs.validator);
      } finally {
        this.uploading = false;
      }
    },

    resetForm() {
      this.form = {
        label: null,
        file: null,
        terms_accepted: false,
      };
    },

    onDeleteConfirmation(id) {
      this.deletingDocumentId = id;
      this.deleteConfirmationVisible = true;
    },

    closeModal() {
      this.deletingDocumentId = null;
      this.deleteConfirmationVisible = false;
    },

    async destroy() {
      try {
        await new ApplicantDocument({
          id: this.deletingDocumentId,
        }).delete();

        await this.$notify.success(this.$t('notifications.document.deleted'));

        this.documents = this.documents.filter((document) => document.id !== this.deletingDocumentId);
        this.closeModal();
      } catch (e) {
        this.$axios.handleError(e);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.file-upload {
  margin-top: $s-2;
  ::v-deep > div {
    width: 100%;
  }
}

.tabs {
  display: flex;
  gap: $s-2;
  margin-bottom: $s-4;
}

.tab {
  padding: $s-4;
  border-bottom: 4px solid var(--outline-color);
  cursor: pointer;
  font-weight: bold;

  &.active {
    border-bottom-color: var(--primary-color);
  }
}

.documents {
  max-height: 40vh;
  overflow-y: auto;
}
</style>
